import { computed, type ComputedRef, h } from 'vue'

import McoStayListLine from '@/stay-list/stay-list-table/mco/McoStayListLine.vue'
import McoStayListColumns from '@/stay-list/stay-list-table/mco/StayListColumns'
import SmrStayListLine from '@/stay-list/stay-list-table/smr/SmrStayListLine.vue'
import SmrStayListColumns from '@/stay-list/stay-list-table/smr/StayListColumns'
import StayListTable from '@/stay-list/stay-list-table/StayListTable.vue'
import { ListStay, StayListLineSlotProps } from '@/stay-list/stay-list-table/types'
import { useStore } from '@/store'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const StayListLineComponents = {
  [PmsiTypeEnum.MCO]: McoStayListLine,
  [PmsiTypeEnum.SMR]: SmrStayListLine
}

const PmsiStayListColumns = {
  [PmsiTypeEnum.MCO]: McoStayListColumns,
  [PmsiTypeEnum.SMR]: SmrStayListColumns,
}

export default function usePmsiStayListTable<TStay extends ListStay>(
  pmsiType: PmsiTypeEnum,
  onOpenQuickView?: (stay: TStay) => void,
  // Only ever set for MCO (quick view is MCO-only, see quickMode.ts) — left undefined for SMR so
  // SmrStayListLine never receives an isQuickViewOpen prop it doesn't declare.
  openQuickViewStayId?: ComputedRef<number | null>
) {
  const store = useStore()

  const stayList = computed(() => store.state.stayList.stayList.items)
  const mode = computed(() => store.state.stayList.stayListParams.mode)

  const params = computed(() => store.state.stayList.stayListParams)

  const requestFetching = computed(() => store.state.stayList.fetchStayListRequest.fetching)

  const updateOrder = (toBeOrderedColumn: string) => store.dispatch('updateStayListOrder', toBeOrderedColumn)

  return () => {
    return h(StayListTable, {
      stayList: stayList.value,
      mode: mode.value,
      pmsiType: pmsiType,
      availableColumns: PmsiStayListColumns[pmsiType],
      order: params.value.order,
      reverseOrder: params.value.reverseOrder,
      isLoading: requestFetching.value,
      onOrderUpdate: (toBeOrderedColumn: string) => updateOrder(toBeOrderedColumn)
    },
    {
      line: (lineProps: StayListLineSlotProps<TStay>) => h(StayListLineComponents[pmsiType], {
        stay: lineProps.stay,
        columns: lineProps.columns,
        allowSelection: lineProps.canSelectStay,
        selected: lineProps.selected,
        mode: mode.value,
        onToggleSelectedStay: lineProps.toggleSelectedStay,
        onOpenQuickView: onOpenQuickView,
        ...(openQuickViewStayId ? { isQuickViewOpen: openQuickViewStayId.value === lineProps.stay.id } : {})
      })
    })
  }
}

